import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  User, 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  signOut,
  sendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendEmailVerification
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc,
  onSnapshot,
  DocumentData 
} from 'firebase/firestore';
import { auth } from '../firebase/firebaseConfig';
import { db } from '../firebase/firebaseConfig';

type AuthContextType = {
  currentUser: User | null;
  userDisplayName: string | null;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  googleSignIn: () => Promise<void>;
  signup: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

type Props = {
  children: ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userDisplayName, setUserDisplayName] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      // Check if user is admin - could be based on email or custom claims
      setIsAdmin(user?.email === process.env.REACT_APP_ADMIN_EMAIL);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setUserDisplayName(null);
      return;
    }

    const userDocRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        const displayName = data.firstName && data.lastName 
          ? `${data.firstName} ${data.lastName}`
          : data.firstName || data.lastName || currentUser.email || 'User';
        setUserDisplayName(displayName);
      } else {
        // If user doc doesn't exist, just use email or default
        setUserDisplayName(currentUser.email || 'User');
      }
    }, (error) => {
      console.error('Error fetching user profile:', error);
      setUserDisplayName(currentUser.email || 'User');
    });

    return unsubscribe;
  }, [currentUser]);

  async function login(email: string, password: string) {
    await signInWithEmailAndPassword(auth, email, password);
  }

  async function logout() {
    await signOut(auth);
  }

  async function resetPassword(email: string) {
    await sendPasswordResetEmail(auth, email);
  }

  async function googleSignIn() {
    await signInWithPopup(auth, new GoogleAuthProvider());
  }

  async function signup(email: string, password: string, firstName: string, lastName: string) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Send email verification
    await sendEmailVerification(user);
    
    // Save user profile to Firestore
    if (user) {
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        email: user.email,
        firstName,
        lastName,
        createdAt: new Date()
      });
    }
  }

  const value = {
    currentUser,
    userDisplayName,
    isAdmin,
    login,
    logout,
    resetPassword,
    googleSignIn,
    signup
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}